import timezoneCities from "~/utils/helpers/timezone-cities.json";
import { UserLanguageEnum } from "~/utils/enums/users.enums";
import type { ITimezoneOption } from "~/utils/interfaces/timezones-interfaces";
import { listTimezones } from "~/utils/helpers/timezones";

/**
 * « (UTC+11:00) Pacifique/Nouméa »: the zone in the user's language (French and German; English keeps the
 * IANA id). The offset and zone names come from `Intl`; the cities from `timezone-cities.json`, generated
 * from CLDR by `scripts/build-timezone-cities.mjs` (don't edit it by hand), since `Intl` can't name a city.
 * Kept out of `timezones.ts` so the participant screens, which only convert hours, don't load this data.
 */

/**
 * V8's ICU still lists these 19 zones under their old name (`Europe/Kiev`). Labels show the current one, or
 * « Kyiv » would find nothing; the value stays the runtime's, the one the browser reports.
 */
const CURRENT_TIMEZONE_NAMES: Record<string, string> = {
  "Africa/Asmera": "Africa/Asmara",
  "America/Buenos_Aires": "America/Argentina/Buenos_Aires",
  "America/Catamarca": "America/Argentina/Catamarca",
  "America/Coral_Harbour": "America/Atikokan",
  "America/Cordoba": "America/Argentina/Cordoba",
  "America/Godthab": "America/Nuuk",
  "America/Indianapolis": "America/Indiana/Indianapolis",
  "America/Jujuy": "America/Argentina/Jujuy",
  "America/Louisville": "America/Kentucky/Louisville",
  "America/Mendoza": "America/Argentina/Mendoza",
  "Asia/Calcutta": "Asia/Kolkata",
  "Asia/Katmandu": "Asia/Kathmandu",
  "Asia/Rangoon": "Asia/Yangon",
  "Asia/Saigon": "Asia/Ho_Chi_Minh",
  "Atlantic/Faeroe": "Atlantic/Faroe",
  "Europe/Kiev": "Europe/Kyiv",
  "Pacific/Enderbury": "Pacific/Kanton",
  "Pacific/Ponape": "Pacific/Pohnpei",
  "Pacific/Truk": "Pacific/Chuuk",
};

/** A browser reporting `Europe/Kyiv` must still find the city CLDR files under `Europe/Kiev`. */
const LEGACY_TIMEZONE_NAMES: Record<string, string> = Object.fromEntries(
  Object.entries(CURRENT_TIMEZONE_NAMES).map(([legacy, current]) => [current, legacy]),
);

/** CLDR mostly lists the cities whose name differs from the id (« Londres »); the others read from the id. */
const CITY_NAMES: Record<UserLanguageEnum, Partial<Record<string, string>>> = {
  [UserLanguageEnum.FR]: timezoneCities.cities.fr,
  [UserLanguageEnum.EN]: timezoneCities.cities.en,
  [UserLanguageEnum.DE]: timezoneCities.cities.de,
};

/** French when the profile's language is one the app doesn't offer. */
function toLanguage(language?: string | null): UserLanguageEnum {
  return Object.values(UserLanguageEnum).find((value) => value === language) ?? UserLanguageEnum.FR;
}

/** Tries the old and new names too: CLDR files a renamed zone under either one, depending on its version. */
function timezoneCity(timezone: string, language: UserLanguageEnum): string {
  const current = CURRENT_TIMEZONE_NAMES[timezone] ?? timezone;
  const legacy = LEGACY_TIMEZONE_NAMES[timezone] ?? timezone;
  const cities = CITY_NAMES[language];

  return (
    cities[timezone] ??
    cities[legacy] ??
    cities[current] ??
    current.slice(current.lastIndexOf("/") + 1).replace(/_/g, " ")
  );
}

/** Kept by hand: CLDR names neither the oceans nor the Arctic, so it can't translate these segments. */
const SEGMENT_NAMES: Record<UserLanguageEnum.FR | UserLanguageEnum.DE, Partial<Record<string, string>>> = {
  [UserLanguageEnum.FR]: {
    Africa: "Afrique",
    America: "Amérique",
    Antarctica: "Antarctique",
    Arctic: "Arctique",
    Asia: "Asie",
    Atlantic: "Atlantique",
    Australia: "Australie",
    Europe: "Europe",
    Indian: "Océan Indien",
    Pacific: "Pacifique",
    Argentina: "Argentine",
    North_Dakota: "Dakota du Nord",
  },
  [UserLanguageEnum.DE]: {
    Africa: "Afrika",
    America: "Amerika",
    Antarctica: "Antarktis",
    Arctic: "Arktis",
    Asia: "Asien",
    Atlantic: "Atlantik",
    Australia: "Australien",
    Europe: "Europa",
    Indian: "Indischer Ozean",
    Pacific: "Pazifik",
    Argentina: "Argentinien",
  },
};

function timezoneDisplayName(timezone: string, language: UserLanguageEnum): string {
  const current = CURRENT_TIMEZONE_NAMES[timezone] ?? timezone;
  if (language === UserLanguageEnum.EN) return current.replace(/_/g, " ");

  const names = SEGMENT_NAMES[language];
  return [
    ...current
      .split("/")
      .slice(0, -1)
      .map((segment) => names[segment] ?? segment.replace(/_/g, " ")),
    timezoneCity(timezone, language),
  ].join("/");
}

/**
 * The offset on the step's date, since summer time changes it. `Intl` renders « GMT+11:00 », and at
 * Greenwich « GMT+00:00 » or a bare « GMT » depending on the engine.
 */
function utcOffset(timezone: string, at: Date): string {
  try {
    const parts = new Intl.DateTimeFormat("en-US", { timeZone: timezone, timeZoneName: "longOffset" }).formatToParts(
      at,
    );
    const raw = parts.find((part) => part.type === "timeZoneName")?.value ?? "";
    const match = raw.match(/^GMT(?:([+-])(\d{2}):(\d{2}))?$/);
    if (!match) return "";

    const [, sign = "+", hours = "00", minutes = "00"] = match;
    return `UTC${sign}${hours}:${minutes}`;
  } catch {
    // A zone the runtime doesn't know (legacy data, a typo) must not break the screen.
    return "";
  }
}

/** The offset as people type it: « utc+1 » is not a substring of « UTC+01:00 ». */
function toTypedOffset(offset: string): string {
  return offset.replace(/^UTC([+-])0?(\d+):(\d{2})$/, (_match, sign: string, hours: string, minutes: string) =>
    minutes === "00" ? `UTC${sign}${hours}` : `UTC${sign}${hours}:${minutes}`,
  );
}

/** What `Intl` renders when it has no name for a zone. */
const OFFSET_ONLY_NAME = /^(?:GMT|UTC)(?:[+\-−]|$)/;

/** Searched, not displayed; `null` when `Intl` only has an offset, which the label already carries. */
function timezoneName(timezone: string, at: Date, language: UserLanguageEnum): string | null {
  try {
    const name = new Intl.DateTimeFormat(language, { timeZone: timezone, timeZoneName: "long" })
      .formatToParts(at)
      .find((part) => part.type === "timeZoneName")?.value;

    return name && !OFFSET_ONLY_NAME.test(name) ? name : null;
  } catch {
    return null;
  }
}

/** Local noon, because `new Date("2026-01-15")` is UTC midnight: the day before for half the globe. */
function toReferenceDate(referenceDate?: string | Date | null): Date {
  if (!referenceDate) return new Date();
  if (referenceDate instanceof Date) return Number.isNaN(referenceDate.getTime()) ? new Date() : referenceDate;

  const [year, month, day] = referenceDate.split(/[-T]/).map(Number);
  if (!year || !month || !day) return new Date();

  return new Date(year, month - 1, day, 12, 0, 0, 0);
}

/** Takes the offset already computed, which the selector's search needs too. */
function toTimezoneLabel(timezone: string, offset: string, language: UserLanguageEnum): string {
  if (!offset) return timezone;

  return `(${offset}) ${timezoneDisplayName(timezone, language)}`;
}

/**
 * Every screen and the selector go through here, so a zone reads the same in read and edit mode. A zone the
 * runtime doesn't know is shown as is.
 */
export function formatTimezoneLabel(
  timezone: string,
  referenceDate?: string | Date | null,
  language?: string | null,
): string {
  return toTimezoneLabel(timezone, utcOffset(timezone, toReferenceDate(referenceDate)), toLanguage(language));
}

/** Accent-free, with `Intl`'s typographic apostrophe (« d’Europe ») turned into the one people type. */
function toSearchable(text: string): string {
  return text
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/’/g, "'");
}

/** Adds an accent-free copy, so that « noumea » finds Nouméa. */
function toSearchText(...parts: (string | null)[]): string {
  const text = parts.filter(Boolean).join(" ");
  const searchable = toSearchable(text);

  return searchable === text ? text : `${text} ${searchable}`;
}

/** `referenceDate` is the step's start date, so the offsets and names are those of the session day. */
export function buildTimezoneOptions(
  referenceDate?: string | Date | null,
  language?: string | null,
): ITimezoneOption[] {
  const at = toReferenceDate(referenceDate);
  const displayLanguage = toLanguage(language);

  return listTimezones().map((timezone) => {
    const offset = utcOffset(timezone, at);
    const label = toTimezoneLabel(timezone, offset, displayLanguage);
    const ianaName = (CURRENT_TIMEZONE_NAMES[timezone] ?? timezone).replace(/_/g, " ");
    const name = timezoneName(timezone, at, displayLanguage);
    const city = timezoneCity(timezone, displayLanguage);

    return {
      value: timezone,
      label,
      searchText: toSearchText(label, toTypedOffset(offset), ianaName, name, timezone, city),
    };
  });
}
